import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ApplicationService } from 'src/app/services/application-service';
import { TranslateService } from '@ngx-translate/core';
import { FlowService } from 'src/app/services/flow.service';
import { ApplicationsApiService } from 'src/app/services/api/applications-api.service';
import { handleApiError } from 'src/app/utils/error';
import { environment } from 'src/environments/local/environment';
import { AuthService } from 'src/app/services/auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { PersonalInfoService } from 'src/app/services/personal-info.service';
import { BasicInfoService } from 'src/app/services/basic-info.service';
import { ContactsService } from 'src/app/services/contacts.service';
import { MatDialog } from '@angular/material/dialog';
import { DialogComponent } from 'src/app/components/shared/dialog/dialog.component';
import { Overlay } from '@angular/cdk/overlay';
import { reasonOptions } from 'src/configs/dropdown-options/options.se';
import { TicketType } from 'src/app/app.consts';
import { ApplicationScoreService } from 'creddo-shared-library';
import {
  excludeGenericFileUploads,
  exclusivelyRetrieveGenericPrefix,
} from 'src/app/utils/filter-functions';

@Component({
  selector: 'app-summary',
  templateUrl: './summary.component.html',
  styleUrls: ['./summary.component.scss'],
})
export class SummaryComponent implements OnInit, OnDestroy {
  applicationId!: number;
  isLoading = false;
  error!: string;
  private subs: Array<Subscription> = [];
  panelOpenStatus: { [key: string]: boolean } = {
    purpose: false,
    guarantor: false,
  };
  isMinasidor = false;
  headerText!: string;
  @ViewChild('guarantor') guarantorExpandPanel!: ElementRef;

  constructor(
    public applicationService: ApplicationService,
    public flowService: FlowService,
    private translateService: TranslateService,
    private applicationApiService: ApplicationsApiService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private personalInfoService: PersonalInfoService,
    private contactsService: ContactsService,
    public basicInfoService: BasicInfoService,
    protected router: Router,
    private dialog: MatDialog,
    private overlay: Overlay,
    private applicationScoreService: ApplicationScoreService
  ) {}

  ngOnInit(): void {
    this.subs.push(
      this.route.queryParams.subscribe((params) => {
        const { applicationId } = params;
        this.isMinasidor = params.token;

        if (this.isMinasidor && applicationId) {
          this.isLoading = true;

          this.applicationApiService.getApplication(applicationId).subscribe(
            (response) => {
              this.personalInfoService.populatePersonalInfo(response);
              this.basicInfoService.populateServiceWithGetApplicationResponseData(
                response
              );
              this.applicationService.populateServiceWithGetApplicationResponseData(
                response
              );
              this.contactsService.populateServiceWithGetApplicationResponseData(
                response
              );
              const flow = this.applicationService.flowId;
              this.flowService.selectFlow(flow);

              this.flowService.setScoringModel(response.flowId);
              this.applicationScoreService.populateFromMinaSidorToLoan(
                response
              );
              this.error = '';
            },
            (error) => {
              handleApiError(error, this.translateService);
            },
            () => {
              this.isLoading = false;
            }
          );
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.map((s) => s.unsubscribe());
  }

  get purposeTitle(): string {
    const option = reasonOptions.find(
      (o) => o.value === this.applicationService.reason
    );
    if (option) {
      return this.translateService.instant(`reasonOptions.${option.display}`);
    } else {
      return this.applicationService.reason;
    }
  }

  get guarantorTitle(): string {
    const selfGuarantor = this.applicationService.selfGuarantor;
    const additionalGuarantors = this.applicationService.additionalGuarantors;

    if (selfGuarantor && additionalGuarantors.length === 1) {
      const first = `${this.translateService.instant('summary.guarantor.me')}`;
      const second = `${
        (additionalGuarantors[0].get('personNumber') as FormControl).value
      }`;
      return `${first}, ${second}`;
    } else if (additionalGuarantors.length === 2) {
      const first = `${
        (additionalGuarantors[0].get('personNumber') as FormControl).value
      }`;
      const second = `${
        (additionalGuarantors[1].get('personNumber') as FormControl).value
      }`;
      return `${first}, ${second}`;
    } else if (additionalGuarantors.length === 1) {
      return `${
        (additionalGuarantors[0].get('personNumber') as FormControl).value
      }`;
    } else if (selfGuarantor) {
      return this.translateService.instant('summary.guarantor.me');
    } else {
      return '';
    }
  }

  get realEstateTitle(): string {
    const realEstateValue = this.applicationService.realEstate;
    const propertyDescription = this.applicationService.propertyDescription;
    const realEstateFiles = this.applicationService.realEstateFiles;

    if (realEstateValue === 2) {
      return this.translateService.instant('realEstate.notExist');
    } else if (realEstateValue === 1) {
      if (propertyDescription) {
        return propertyDescription;
      } else if (realEstateFiles) {
        const names: Array<string> = [];
        realEstateFiles.map((file: any) => {
          const name = file.name;
          names.push(name);
        });

        return names.join(', ');
      }
    }
    return '';
  }

  get balanceTitle(): string {
    const balanceFiles = this.applicationService.balance;
    if (balanceFiles) {
      return this.extractFileNames(balanceFiles);
    }
    return '';
  }

  get profitAndLossTitle(): string {
    const profitAndLossFiles = this.applicationService.profitAndLoss;
    if (profitAndLossFiles) {
      return this.extractFileNames(profitAndLossFiles);
    }
    return '';
  }

  get accountDataTitle(): string {
    const accountDataFiles = this.applicationService.accountData;
    const klarnaUpload = this.applicationService.klarnaUpload;
    if (accountDataFiles) {
      return this.extractFileNames(accountDataFiles);
    } else if (klarnaUpload) {
      return (
        this.translateService.instant('accountData.klarna.uploadedFileLabel') +
        ' Klarna'
      );
    }
    return '';
  }

  get otherSecuritiesPanelTitle(): string {
    const other = this.applicationService.other;
    const otherFiles = excludeGenericFileUploads(
      this.applicationService.otherSecuritiesFiles
    );

    if (other) {
      return other;
    } else if (otherFiles) {
      return this.extractFileNames(otherFiles);
    }
    return '';
  }

  generalFilesPanelTitle(currentRoute: string): string {
    const slicedRoute = currentRoute.slice(1);
    const generalFiles = exclusivelyRetrieveGenericPrefix(
      this.applicationService.genericFiles,
      slicedRoute
    );

    if (generalFiles) {
      const regex = new RegExp(`gfu_${slicedRoute}_`, 'gi');
      const sliced = this.extractFileNames(generalFiles).replace(regex, '');
      return sliced;
    }
    return '';
  }

  get isBigTicket(): boolean {
    if (this.basicInfoService.ticketType === TicketType.BIG) {
      return true;
    }
    return false;
  }

  extractFileNames(files: Array<any>): string {
    const names: Array<string> = [];
    files.map((file: any) => {
      const name = file.name || file.filename;
      names.push(name);
    });

    return names.join(', ');
  }

  handleOpenPanel(panel: string): void {
    this.panelOpenStatus[panel] = true;
  }

  handleClosePanel(panel: string): void {
    this.panelOpenStatus[panel] = false;
  }

  handleButtonClick(): void {
    if (
      !this.applicationService.selfGuarantor &&
      this.applicationService.additionalGuarantors.length === 0 &&
      !this.isBigTicket
    ) {
      this.openDialog();
    } else {
      this.handleConfirmApplication();
    }
  }

  openDialog(): void {
    const dialogRef = this.dialog.open(DialogComponent, {
      maxWidth: '400px',
      data: {
        title: this.translateService.instant('summary.emptyGuarantor.title'),
        question: this.translateService.instant('summary.emptyGuarantor.body'),
        yes: this.translateService.instant('summary.emptyGuarantor.yes'),
        no: this.translateService.instant('summary.emptyGuarantor.no'),
      },
      autoFocus: false,
      scrollStrategy: this.overlay.scrollStrategies.noop(),
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.handleConfirmApplication();
      } else {
        this.handleOpenPanel('guarantor');
        this.guarantorExpandPanel.nativeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    });
  }

  handleConfirmApplication(): void {
    if (this.isMinasidor) {
      window.location.assign(
        `${environment.summaryRedirectUrl}?jwt=${this.authService.accessToken}`
      );
    } else if (this.isBigTicket) {
      window.location.replace(
        `${environment.summaryRedirectUrl}?jwt=${this.authService.accessToken}`
      );
    } else {
      this.applicationApiService
        .submitApplicationForProcessing({
          applicationId: this.applicationService.applicationId,
        })
        .subscribe(
          () => {
            const redirectUrl = `${environment.summaryRedirectUrl}?jwt=${this.authService.accessToken}`;
            window.location.replace(redirectUrl);
          },
          (error) => {
            this.error = handleApiError(error, this.translateService);
            this.isLoading = false;
          }
        );
    }
  }

  get country(): string {
    return environment.country;
  }

  get barometerTitle(): string {
    if (this.applicationScoreService.currentAppScore > 65) {
      return this.translateService.instant('summary.barometer.title.strong');
    }
    return this.translateService.instant('summary.barometer.title.weak');
  }

  get barometerContent(): string {
    if (this.applicationScoreService.currentAppScore > 65) {
      return this.translateService.instant('summary.barometer.content.strong');
    }
    return this.translateService.instant('summary.barometer.content.weak');
  }
}
